<template>
  <component-wrapper
    ref="seminarsOverview"
    class="seminars-overview-fi"
  >
    <template v-if="!activeRegistration">
      <template v-if="momentLoaded && (getAssignmentAndEvents || isEditMode)">
        <tab-navigation-fi
          v-if="(hasSeminars && hasELearnings) || isEditMode"
          ref="tabNavi"
          :tab-navigation="tabNavigation"
          :content-loading="seminarsLoading"
          :init-id="initTab"
          hide-only
          @change="tabChanged"
        >
          <template #[getTabName(0)]>
            <seminar-assignments-fi
              ref="assignments"
              key="assignments"
              :lms-data="getAssignmentAndEvents.seminars"
              @filters-changed="filtersChanged"
              @register="showRegistration"
            />
          </template>
          <template #[getTabName(1)]>
            <seminar-elearnings-fi
              ref="elearnings"
              key="eLearnings"
              :lms-data="getAssignmentAndEvents.eLearnings"
              @filters-changed="filtersChanged"
            />
          </template>
        </tab-navigation-fi>

        <seminar-assignments-fi
          v-else-if="hasSeminars"
          ref="assignments"
          key="assignments"
          :lms-data="getAssignmentAndEvents.seminars"
          @filters-changed="filtersChanged"
          @register="showRegistration"
        />

        <seminar-elearnings-fi
          v-else-if="hasELearnings"
          ref="elearnings"
          key="eLearnings"
          :lms-data="getAssignmentAndEvents.eLearnings"
          @filters-changed="filtersChanged"
        />
      </template>
      <div
        v-else
        class="seminars-overview-fi__loading"
      >
        <loading-indicator variant="spinnerLocal" />
      </div>
    </template>
    <seminar-registration-fi
      v-else
      :assignment="activeRegistrationAssignment"
      :active-id="activeRegistrationEventId || ''"
      @cancel="cancelRegistration"
      @exit="exitRegistration"
    />
  </component-wrapper>
</template>

<script setup lang="ts">
import { useQuery } from '@urql/vue';
import { LoadingIndicator } from 'atoms';
import { useLoginStore } from 'store/login';
import { computed, nextTick, onServerPrefetch, ref, useTemplateRef, watch } from 'vue';

import ComponentWrapper from '@/components/component-wrapper/ComponentWrapper.vue';
import SeminarRegistrationFi from '@/components/seminar-assignments-fi/seminar-registration-fi/SeminarRegistrationFi.vue';
import SeminarAssignmentsFi from '@/components/seminar-assignments-fi/SeminarAssignmentsFi.vue';
import { usePendingRegistration } from '@/components/seminar-assignments-fi/usePendingRegistration';
import { useSeminarRestore } from '@/components/seminar-assignments-fi/useSeminarRestore';
import SeminarElearningsFi from '@/components/seminar-elearnings-fi/SeminarElearningsFi.vue';
import type { TabNavigation } from '@/components/tab-navigation-fi/TabNavigationFi.vue';
import TabNavigationFi from '@/components/tab-navigation-fi/TabNavigationFi.vue';
import { useDate } from '@/composables/date';
import { useFischer } from '@/composables/fischerPlugin';
import GETASSIGNMENTANDEVENTS from '@/graphql/getAssignmentAndEvents.gql';
import type { SingleLineTextField } from '@/sitecoreFieldTypes';
import { scrollToElement } from '@/utils/smoothScroll';

defineOptions({
  name: 'SeminarsOverviewFi',
});

const fischer = useFischer();
const { momentLoaded, momentLoadedPromise } = useDate();
const loginStore = useLoginStore();
const { consumeSeminarRestore, saveSeminarRestore } = useSeminarRestore();
const { savePendingRegistration } = usePendingRegistration();

const tabNavi = useTemplateRef<typeof TabNavigationFi>('tabNavi');
const assignments = useTemplateRef<typeof SeminarAssignmentsFi>('assignments');
const elearnings = useTemplateRef<typeof SeminarElearningsFi>('elearnings');
const seminarsOverview = useTemplateRef<typeof ComponentWrapper>('seminarsOverview');

const props = withDefaults(
  defineProps<{
    fields?: {
      tabLabelSeminars?: SingleLineTextField;
      tabLabelELearning?: SingleLineTextField;
      userId?: {
        value: string | number;
      };
      language?: SingleLineTextField;
      eventLanguage?: SingleLineTextField;
    };
  }>(),
  {
    fields: () => ({}),
  },
);

type TabIndex = 0 | 1;

const seminarsLoading = ref<boolean>(true);
const initTab = ref<number>(0);
const getAssignmentAndEvents = ref<any>(null);
const activeRegistration = ref<boolean>(false);
const activeRegistrationAssignment = ref<any>(null);
const activeRegistrationEventId = ref<string | null>(null);

const tabNavigation = computed<TabNavigation[]>(() => [
  { id: 0, text: props.fields?.tabLabelSeminars?.value || 'seminars' },
  { id: 1, text: props.fields?.tabLabelELearning?.value || 'e-learnning' },
]);

const variables = computed(() => ({
  userId: props.fields?.userId?.value.toString(),
  language: props.fields?.language?.value,
  eventLanguage: props.fields?.eventLanguage?.value,
}));
const { data, error, executeQuery } = useQuery({
  query: GETASSIGNMENTANDEVENTS,
  variables,
});
onServerPrefetch(async () => {
  await executeQuery();
});

const hasSeminars = computed<boolean>(() =>
  Boolean(getAssignmentAndEvents.value?.seminars?.events?.event?.length),
);

const hasELearnings = computed<boolean>(() =>
  Boolean(getAssignmentAndEvents.value?.eLearnings?.assignments?.assignment?.length),
);

const scrollToSeminarId = (id: string) => {
  let scrollToElearning = false;
  if (hasELearnings.value) {
    const filteredElearning =
      getAssignmentAndEvents.value?.eLearnings?.assignments?.assignment?.filter(
        (el: any) => el.assignment.assignmentId[0].id === id,
      );
    scrollToElearning = Boolean(filteredElearning?.length);
  }

  if (scrollToElearning) {
    initTab.value = 1;
    setTimeout(() => elearnings.value?.scrollTo(id, 0), 500);
  } else {
    setTimeout(() => assignments.value?.scrollTo(id, 0), 500);
  }
};

const restoreSeminarIdInUrl = (id: string) => {
  if (IS_SSR) {
    return;
  }
  const params = new URLSearchParams(window.location.search);
  if (params.get('seminar-id') === id) {
    return;
  }
  params.set('seminar-id', id);
  window.history.replaceState('', '', `?${params}`);
};

const applyUrlParams = async (storedSearch: string | null) => {
  const allParams =
    storedSearch && !window.location.search
      ? Object.fromEntries(new URLSearchParams(storedSearch))
      : JSON.parse(JSON.stringify(fischer.query()));

  const seminarId = allParams['seminar-id'];

  const tabName = allParams._tab;
  const foundTab = tabNavigation.value.find((nav) => nav.text === tabName);

  const filters = { ...allParams };
  delete filters._tab;
  delete filters['seminar-id'];

  if (!foundTab) {
    if (assignments.value) {
      await assignments.value.setUrlFilters(filters);
    } else if (elearnings.value) {
      await elearnings.value.setUrlFilters(filters);
    }
  } else {
    initTab.value = foundTab.id;

    if (foundTab.id === 0) {
      if (assignments.value) {
        await assignments.value.setUrlFilters(filters);
      } else {
        console.error('could not find assignment ref to restore');
      }
    } else if (foundTab.id === 1) {
      if (elearnings.value) {
        await elearnings.value.setUrlFilters(filters);
      } else {
        console.error('could not find elearnings ref to restore');
      }
    } else {
      console.error('unknown tab id', foundTab);
    }
  }

  if (seminarId) {
    restoreSeminarIdInUrl(seminarId);
    scrollToSeminarId(seminarId);
  }
};

const restoreUrlParams = async () => {
  if (IS_SSR) {
    applyUrlParams(null);
    return;
  }

  await momentLoadedPromise;
  await nextTick();

  console.debug('[seminar-filter-restore] loginStore.loggedIn', loginStore.loggedIn);

  if (loginStore.loggedIn) {
    applyUrlParams(consumeSeminarRestore());
    return;
  }
  applyUrlParams(null);

  // login state may not be set yet — watch for it and restore once it arrives
  console.debug('[seminar-filter-restore] waiting for loginStore.loggedIn...');
  watch(
    () => loginStore.loggedIn,
    (loggedIn) => {
      console.debug('[seminar-filter-restore] loginStore.loggedIn changed to', loggedIn);
      if (loggedIn) {
        applyUrlParams(consumeSeminarRestore());
      }
    },
    { once: true },
  );
};

// immediate + flush:'sync' - see createSsrExchange. immediate: a restored SSR result is
// delivered during setup, so a lazy watcher never sees it. sync: Vue drops non-sync watchers
// during SSR entirely, so without it this state is never filled on the server.
// immediate: restored SSR results are delivered synchronously during setup, so a lazy watcher
// would never see them and the list would stay stuck on seminarsLoading after hydration. Both
// scrollToSeminarId and restoreUrlParams defer their DOM work (setTimeout / await nextTick),
// so running them from setup is safe.
watch(
  data,
  async (d) => {
    if (!d) return;
    getAssignmentAndEvents.value = d?.getAssignmentAndEvents;
    seminarsLoading.value = false;

    if (IS_SSR) {
      return;
    }

    const scrollToId = fischer.query('seminar-id');
    if (scrollToId) {
      scrollToSeminarId(scrollToId);
    } else {
      restoreUrlParams();
    }
  },
  { immediate: true, flush: 'sync' },
);
watch(error, (e) => {
  if (e) {
    seminarsLoading.value = false;
    console.error(e);
  }
});

const getTabName = (idx: TabIndex) => {
  return `tab-content-${tabNavigation.value[idx].id}`;
};

const getTab = () => {
  if ((hasSeminars.value && hasELearnings.value) || fischer.app.isEditMode()) {
    const tab = tabNavi.value?.activeItem;
    if (!tab) {
      console.error('Could not get active tab');
    }
    return tab;
  }
  if (hasSeminars.value) {
    return { id: 0 };
  }
  if (hasELearnings.value) {
    return { id: 1 };
  }
  return null;
};

const filtersChanged = (): void => {
  const search = new URLSearchParams();

  const tab = getTab();
  if (!tab) {
    return;
  }
  if (tab.text) {
    search.append('_tab', tab.text);
  }

  type Element = {
    name: string;
    selected: boolean;
    disabled: boolean;
    facetId: number;
  };

  type Facet = {
    type: string;
    name: string;
    elements?: Element[];
  };

  if (tab.id === 0) {
    const seminars = assignments.value;
    if (seminars) {
      seminars.assignmentFacets?.forEach((filter: Facet) => {
        if (filter.type === 'date') {
          filter.elements?.forEach((element) => {
            if (element.selected) {
              search.append(filter.name, element.name);
            }
          });
        } else if (filter.elements) {
          filter.elements.forEach((element) => {
            if (element.selected && !element.disabled) {
              search.append(filter.name, element.name);
            }
          });
        } else {
          console.error('filter has no elements', filter);
        }
      });
    } else {
      console.error('could not find assignment ref on change');
    }
  } else if (tab.id === 1) {
    const seminars = elearnings.value;
    if (seminars) {
      seminars.activeFilters?.forEach((filter: Element) => {
        if (filter.selected && !filter.disabled) {
          const facet = seminars.eventFacets.find((eventFacet: Facet) =>
            eventFacet.elements?.some((element: Element) => element.facetId === filter.facetId),
          );
          if (facet) {
            search.append(facet.name, filter.name);
          } else {
            console.error('Could not find e-learning facet', filter.facetId);
          }
        }
      });
    } else {
      console.error('could not find elearnings ref on change');
    }
  } else {
    console.error('unknown tab id', tab.id);
  }

  window.history.replaceState('', '', `?${search}`);
};

const tabChanged = (): void => {
  filtersChanged();
};

const showRegistration = async ({ assignment, eventId }: { assignment: any; eventId: string }) => {
  if (!fischer.user.accessToken()) {
    savePendingRegistration({ assignmentId: assignment.assignmentId, eventId });
    saveSeminarRestore(window.location.search);
    loginStore.triggerLogin();
    return;
  }

  activeRegistration.value = true;
  activeRegistrationAssignment.value = assignment;
  activeRegistrationEventId.value = eventId;
  await nextTick();
  scrollToElement(seminarsOverview.value?.$el ?? document.body);
};

const cancelRegistration = async () => {
  const assignmentId = activeRegistrationAssignment.value?.assignmentId;
  activeRegistration.value = false;
  activeRegistrationAssignment.value = null;
  activeRegistrationEventId.value = null;
  await nextTick();
  setTimeout(() => {
    if (initTab.value === 1) {
      elearnings.value?.scrollTo(assignmentId, 300);
    } else {
      assignments.value?.scrollTo(assignmentId, 300);
    }
  }, 500);
};

const exitRegistration = async () => {
  activeRegistration.value = false;
  activeRegistrationAssignment.value = null;
  activeRegistrationEventId.value = null;
};
</script>

<style scoped src="./seminars-overview-fi" />
